import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { uiTranslations } from '../data/translations';
import { cvData } from '../data/cvData';
import SafeIcon from '../common/SafeIcon';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const t = uiTranslations[language].nav;
  const data = cvData[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t.about, href: '#about' },
    { name: t.experience, href: '#experience' },
    { name: t.skills, href: '#skills' },
    { name: t.documents, href: '#documents' },
    { name: t.workstyle, href: '#workstyle' },
    { name: t.personal, href: '#personal' },
    { name: t.contact, href: '#contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    // Update URL Hash for GTM Tracking
    window.history.pushState(null, null, href);
    
    const element = document.querySelector(href);
    if (element) {
      const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm py-4' : 'bg-white/90 backdrop-blur-md py-6'}`}>
      <div className="max-w-6xl mx-auto px-6 md:px-12 flex justify-between items-center">
        <a 
          href="#hero" 
          onClick={(e) => scrollToSection(e, '#hero')} 
          className="text-xl font-bold text-slate-900 tracking-tight"
        >
          {data.personal.name}
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex space-x-6 items-center">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={(e) => scrollToSection(e, link.href)}
              className="text-sm font-medium text-slate-600 hover:text-primary transition-colors"
            >
              {link.name}
            </a>
          ))}

          {/* Language Switcher Desktop */}
          <div className="flex items-center gap-2 border-l border-slate-200 pl-6 ml-2">
            <button 
              onClick={() => toggleLanguage('de')}
              className={`flex items-center gap-1.5 text-xs font-bold transition-all ${language === 'de' ? 'text-accent scale-105' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <span>🇩🇪</span> DE
            </button>
            <span className="text-slate-200 text-[10px]">|</span>
            <button 
              onClick={() => toggleLanguage('en')}
              className={`flex items-center gap-1.5 text-xs font-bold transition-all ${language === 'en' ? 'text-accent scale-105' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <span>🇬🇧</span> EN
            </button>
          </div>
        </div>

        {/* Mobile Toggle */}
        <div className="flex items-center gap-4 md:hidden">
          <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100">
            <button onClick={() => toggleLanguage('de')} className={`text-xs font-bold ${language === 'de' ? 'text-accent' : 'text-slate-400'}`}>DE</button>
            <span className="text-slate-200 text-[10px]">|</span>
            <button onClick={() => toggleLanguage('en')} className={`text-xs font-bold ${language === 'en' ? 'text-accent' : 'text-slate-400'}`}>EN</button>
          </div>
          <button 
            className="text-slate-900 p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menü umschalten"
          >
            <SafeIcon name={isMobileMenuOpen ? 'X' : 'Menu'} className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-white shadow-lg border-t border-slate-100 px-6 py-4 flex flex-col space-y-4">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={(e) => scrollToSection(e, link.href)}
              className="text-base font-medium text-slate-600 hover:text-primary"
            >
              {link.name}
            </a>
          ))}
        </div>
      )}
    </nav>
  );
}